import { useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { FileUIPart, TextUIPart } from 'ai';
import type { Message } from '@rossum/fabry-api-client';
import { getRossumAgentUrl, isMrFabryAgent } from '../api';
import type { AgentChat } from './chatStore';
import { chatStore } from './chatStore';
import { getChatMetadata } from './UseAgentChat/schemas';
import type { ChatUIMessage } from './UseAgentChat/schemas';
import {
  createChat,
  fetchChatMessages,
  fetchThreadMessages,
} from './useChatApi';

// Legacy service only: flatten its `string | (text | image)[]` content into AI
// SDK parts. mr-fabry instead serves canonical UIMessage[] from `/api/v2`, so it
// never goes through here.
const toUIParts = (
  content: Message['content']
): Array<TextUIPart | FileUIPart> => {
  if (typeof content === 'string') {
    return [{ type: 'text', text: content }];
  }
  return content.map(part =>
    'data' in part
      ? ({
          type: 'file',
          mediaType: part.media_type,
          url: `data:${part.media_type};base64,${part.data}`,
        } satisfies FileUIPart)
      : ({ type: 'text', text: part.text } satisfies TextUIPart)
  );
};

export const useCreateNewChatClient = () => {
  const { mutate, isLoading, error } = useMutation({
    mutationFn: async () => createChat(),
  });

  const create = useCallback(
    (onSuccess: (client: AgentChat) => void, onError?: () => void) =>
      mutate(undefined, {
        onSuccess: response =>
          onSuccess(chatStore.getState().createClient(response.chat_id)),
        onError,
      }),
    [mutate]
  );

  return { create, isLoading, error };
};

export const useLoadChatClient = () => {
  const queryClient = useQueryClient();

  const { mutate, isLoading } = useMutation({
    mutationFn: async (chatId: string) => {
      // Files still come from v1 for both services (no v2 files endpoint yet).
      // On mr-fabry we additionally load messages from v2 as canonical
      // UIMessage[]; the legacy service only has v1, so we flatten its content.
      const [chatResponse, threadMessages] = await Promise.all([
        queryClient.fetchQuery(
          ['chatMessages', chatId],
          () => fetchChatMessages(chatId),
          { staleTime: Infinity }
        ),
        isMrFabryAgent()
          ? queryClient.fetchQuery(
              ['threadMessages', chatId],
              () => fetchThreadMessages(chatId),
              { staleTime: Infinity }
            )
          : Promise.resolve(null),
      ]);

      const loadedMessages: ChatUIMessage[] = threadMessages
        ? threadMessages
            .filter(msg => msg.role === 'user' || msg.role === 'assistant')
            .map(msg => ({
              id: msg.id,
              role: msg.role,
              parts: msg.parts,
              metadata: getChatMetadata(msg.metadata),
            }))
        : chatResponse.messages.map((msg, index) => ({
            id: `${chatId}-${msg.role}-${index}`,
            role: msg.role,
            parts: toUIParts(msg.content),
            metadata:
              msg.feedback != null ? { feedback: msg.feedback } : undefined,
          }));

      const outputFiles = chatResponse.files.map(file => ({
        filename: file.filename,
        url: `${getRossumAgentUrl()}/api/v1/chats/${chatId}/files/${file.filename}`,
      }));

      return { loadedMessages, outputFiles };
    },
  });

  const load = useCallback(
    (chatId: string, onSuccess: (client: AgentChat) => void) => {
      const state = chatStore.getState();
      const existingClient = state.clients[chatId];

      if (existingClient) {
        return onSuccess(existingClient);
      }

      return mutate(chatId, {
        onSuccess: ({ outputFiles, loadedMessages }) => {
          const client = chatStore.getState().createClient(chatId);
          client.hydrateSession(loadedMessages, outputFiles);
          return onSuccess(client);
        },
      });
    },
    [mutate]
  );

  return { load, isLoading };
};
